import { createTheme } from "@mui/material/styles";

// Camp Australia Brand Colors (matching SCSS variables)
const caColors = {
  primary: "#5CE5BB",
  secondary: "#1D4E5C",
  dark: "#0F2B33",
  textPrimary: "#000000",
  textSecondary: "#1D4E5C",
  background: "#FFFFFF",
  backgroundLight: "#F6F7F9",
  backgroundLighter: "#E2E8EB",
  headerGreen: "#5CE5BB", // Green header background
};

// Camp Australia Fonts
const caFonts = {
  display: "ABC Whyte",
  displayWeight: 500, // Medium weight
  body: "ABC Whyte",
};

// Create theme using our brand variables
export const theme = createTheme({
  shape: {
    borderRadius: 0, // Global border radius override to make everything square
  },
  palette: {
    primary: {
      main: caColors.primary,
      contrastText: caColors.textPrimary,
    },
    secondary: {
      main: caColors.secondary,
      contrastText: caColors.background,
    },
    background: {
      default: caColors.backgroundLight,
      paper: caColors.background,
    },
    text: {
      primary: caColors.textPrimary,
      secondary: caColors.textSecondary,
    },
  },
  typography: {
    fontFamily: caFonts.body,
    h1: {
      fontFamily: caFonts.display,
      fontWeight: caFonts.displayWeight,
      fontSize: "2.5rem",
    },
    h2: {
      fontFamily: caFonts.display,
      fontWeight: caFonts.displayWeight,
      fontSize: "2rem",
    },
    h3: {
      fontFamily: caFonts.display,
      fontWeight: caFonts.displayWeight,
      fontSize: "1.75rem",
    },
    h4: {
      fontFamily: caFonts.display,
      fontWeight: caFonts.displayWeight,
      fontSize: "1.5rem",
    },
    h5: {
      fontFamily: caFonts.display,
      fontWeight: caFonts.displayWeight,
      fontSize: "1.25rem",
    },
    h6: {
      fontFamily: caFonts.display,
      fontWeight: caFonts.displayWeight,
      fontSize: "1.125rem",
    },
    body1: {
      fontFamily: caFonts.body,
      fontSize: "1rem",
    },
    body2: {
      fontFamily: caFonts.body,
      fontSize: "1rem", // Changed from 0.875rem (14px) to 1rem (16px) for mobile accessibility
    },
    button: {
      fontFamily: caFonts.body,
      fontWeight: 700,
      textTransform: "none",
    },
  },
  components: {
    // Force all components to be square
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0, // Square corners
          padding: "12px 24px",
          transition: "all 0.2s ease",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0, // Square corners
          boxShadow: "0 2px 8px rgba(29, 78, 92, 0.08)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "16px", // Rounded corners for cards
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0, // Square corners
          zIndex: 1201, // Above drawer
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0, // Square corners
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 0, // Square corners
          width: "44px", // Mobile-friendly touch target (Google Stitch)
          height: "44px", // Mobile-friendly touch target (Google Stitch)
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          borderRadius: 0, // Square corners
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: "#000000",
          },
        },
      },
    },
    // Mobile-friendly touch targets (Google Stitch pattern)
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: "48px", // Increased from 36px to meet 44px minimum touch target (with padding = 48px total)
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: "1rem", // 16px for mobile accessibility
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontSize: "1rem", // 16px for mobile accessibility
          lineHeight: 1.5, // Proper line-height for better touch target height
        },
      },
    },
    // EN-592: Radio buttons should use dark green (secondary) color instead of mint (primary)
    MuiRadio: {
      styleOverrides: {
        root: {
          color: caColors.secondary, // Use dark green instead of primary mint
          "&.Mui-checked": {
            color: caColors.secondary, // Keep dark green when selected
          },
        },
      },
    },
    // EN-592: Checkbox should also use dark green (secondary) color for consistency
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: caColors.secondary, // Use dark green instead of primary mint
          "&.Mui-checked": {
            color: caColors.secondary, // Keep dark green when selected
          },
        },
      },
    },
  },
});
